package app.nestbridge.app.util

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import kotlin.coroutines.cancellation.CancellationException

/**
 * Launches [block] in this scope, guarding against uncaught non-cancellation errors.
 *
 * Circuit presenters run their side effects inside coroutines whose failures are
 * otherwise unhandled — an escaping [Throwable] (network timeout, 5xx, serialization
 * error, etc.) crashes the app. This wrapper:
 *
 *  - Re-throws [CancellationException] untouched, so cooperative cancellation and our
 *    auth-gate signal ([com.karawek.core.network.SessionExpiredException], which IS a
 *    CancellationException) keep working exactly as before.
 *  - Routes every other [Throwable] to [onError] instead of letting it escape, so a
 *    transient backend failure surfaces as a no-op (or a reset loading flag) rather
 *    than a crash.
 *
 * [onError] defaults to a swallow; callers that own loading/saving state should pass a
 * handler that resets those flags so the UI doesn't hang on a spinner after a failure.
 */
inline fun CoroutineScope.launchSafely(
    crossinline onError: (Throwable) -> Unit = {},
    crossinline block: suspend CoroutineScope.() -> Unit,
): Job = launch {
    try {
        block()
    } catch (c: CancellationException) {
        throw c
    } catch (t: Throwable) {
        onError(t)
    }
}
